import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { AIConversationType } from '../../../extViewmodels/sdk/models/AI';
import { useAIAgentTemplatesQuery } from '../../../queries/AIAgentTemplate/useAIAgentTemplatesQuery';
import { AiButton } from '../../../web/components-v2/AiButton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components/Select';
import { SimpleTooltip, TooltipProvider } from '../../../web/components/Tooltip';
import { brandPrimary } from '../../../web/styles/colors';
import { IDemoRecording } from '../../queries/DemoRecordings/useDemoRecordings';
import { PlayIcon } from '../svgs/icons/audio/PlayIcon';
import { TranscriptionIcon } from '../svgs/icons/TranscriptionIcon';
import { styleSheet } from './styles';

interface IRecordingItemProps {
	recording: IDemoRecording;
	isBestMatch: boolean;
	onPlay?: (url: string) => void;
	onDownloadTranscript?: (url: string) => void;
	onAnalyze?: (recording: IDemoRecording, templateId?: string) => void;
	isAnalyzeLoading?: boolean;
}

export const RecordingItem: React.FC<IRecordingItemProps> = ({
	recording,
	isBestMatch,
	onPlay,
	onDownloadTranscript,
	onAnalyze,
	isAnalyzeLoading = false,
}) => {
	const startTime = moment(recording.startTime);
	const formattedTime = startTime.format('h:mm A');
	const formattedDate = startTime.format('MMM D, YYYY');
	// Format duration as "X hours Y minutes" or just "Y minutes" if less than 1 hour
	const formatDuration = (minutes: number): string => {
		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;

		return hours > 0 ? (remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`) : `${remainingMinutes}m`;
	};

	const formattedDuration = formatDuration(recording.duration);

	const handlePlay = () => {
		// Use actualVideoRecording first, fallback to original videoRecording
		if (videoRecording && onPlay) {
			// Prioritize shareUrl (meeting share url) over playUrl (view online)
			const shareUrl = videoRecording.shareUrl;
			const playUrl = videoRecording.playUrl;
			const downloadUrl = videoRecording.downloadUrl;

			// Choose the best URL: shareUrl > playUrl > downloadUrl
			const urlToUse = shareUrl || playUrl || downloadUrl;

			if (urlToUse) {
				onPlay(urlToUse);
			}
		}
	};

	const handleDownloadTranscript = () => {
		// Use actualTranscript first, fallback to original transcript
		if (transcript?.downloadUrl && onDownloadTranscript) {
			onDownloadTranscript(transcript.downloadUrl);
		}
	};

	const handleAnalyze = () => {
		if (onAnalyze) {
			onAnalyze(recording, selectedTemplateId || undefined);
		}
	};

	// Get the closer from internal participants (longest duration participant)
	const closerParticipant = recording.participants
		.filter(p => p.name && p.name.trim())
		.sort((a, b) => b.duration - a.duration)[0]; // Sort by duration descending, take first

	const closerName = closerParticipant?.name || 'Unknown Closer';

	const externalParticipantNames = [
		...new Set(recording.externalParticipants.map(p => p.name).filter(name => name && name.trim())),
	].join(', ');

	const externalParticipantEmails = [
		...new Set(recording.externalParticipants.map(p => p.email).filter(email => email && email.trim())),
	].join(', ');

	const videoRecording = recording.actualVideoRecording || recording.videoRecording;
	// Show play button if we have any playable URL
	const hasPlayableUrl =
		videoRecording && (videoRecording.shareUrl || videoRecording.playUrl || videoRecording.downloadUrl);

	const transcript = recording.actualTranscript || recording.transcript;

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

	// Fetch AI agent templates for the dropdown (both active and inactive)
	const templatesQuery = useAIAgentTemplatesQuery();
	const filteredTemplates = useMemo(() => {
		if (!templatesQuery.data) return [];
		return templatesQuery.data.filter(
			template => template.conversationType === AIConversationType.SalesDemoCallCoaching
		);
	}, [templatesQuery.data]);

	const getTemplateDisplayLabel = () => {
		if (!selectedTemplateId) return 'Default';
		const selectedTemplate = filteredTemplates.find(t => t.id === selectedTemplateId);
		return selectedTemplate?.name || 'Default';
	};

	const handleTemplateChange = (newValue: string) => {
		setSelectedTemplateId(newValue === 'default' ? null : newValue);
	};

	return (
		<div className={css(styleSheet.recordingItem, isBestMatch && styleSheet.bestMatch)}>
			{isBestMatch && <div className={css(styleSheet.bestMatchBadge)}>Most Likely Match</div>}

			<div className={css(styleSheet.actionsContainer)}>
				{onAnalyze && (
					<div className={css(styleSheet.analyzeActions)}>
						<Select value={selectedTemplateId || 'default'} onValueChange={handleTemplateChange}>
							<SelectTrigger styles={[styleSheet.templateSelect]}>
								<SelectValue render={() => <span>{getTemplateDisplayLabel()}</span>} />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value='default'>Default</SelectItem>
								{filteredTemplates.map(template => (
									<SelectItem key={template.id} value={template.id}>
										{template.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<AiButton
							title='Analyze'
							onClick={handleAnalyze}
							isLoading={isAnalyzeLoading}
							disabled={isAnalyzeLoading}
							size='sm'
						/>
					</div>
				)}
				<TooltipProvider>
					{hasPlayableUrl && (
						<SimpleTooltip title='Play Recording'>
							<button
								className={css(styleSheet.iconButton, styleSheet.playIconButton)}
								onClick={handlePlay}
								type='button'
							>
								<PlayIcon fillColor={brandPrimary} />
							</button>
						</SimpleTooltip>
					)}
					{transcript?.downloadUrl && (
						<SimpleTooltip title='Download Transcript'>
							<button className={css(styleSheet.iconButton)} onClick={handleDownloadTranscript} type='button'>
								<TranscriptionIcon />
							</button>
						</SimpleTooltip>
					)}
				</TooltipProvider>
			</div>

			<div className={css(styleSheet.recordingHeader)}>
				<div className={css(styleSheet.timeInfo)}>
					<div>
						<span className={css(styleSheet.date)}>{formattedDate}</span>
						<span className={css(styleSheet.time)}> at {formattedTime} ET</span>
					</div>
					<span className={css(styleSheet.duration)}>• {formattedDuration}</span>
				</div>
			</div>

			{recording.topic && <div className={css(styleSheet.topic)}>{recording.topic}</div>}

			{closerParticipant && (
				<div className={css(styleSheet.participants)}>
					<span className={css(styleSheet.participantLabel)}>Closer:</span>
					<span className={css(styleSheet.participantNames)} title={closerParticipant.email}>
						{closerName}
					</span>
				</div>
			)}

			{externalParticipantNames && (
				<div className={css(styleSheet.participants)}>
					<span className={css(styleSheet.participantLabel)}>Attendees:</span>
					<span className={css(styleSheet.participantNames)} title={externalParticipantEmails}>
						{externalParticipantNames}
					</span>
				</div>
			)}
		</div>
	);
};
